import { INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE } from "@cart-client/contexts/CartHeaderLottieContext";
import { IUpdateCartHeaderState } from "@cart-client/hooks/useCommonCartheaderLottie/types";
import { isEmpty } from "@swiggy-private/js-utils/dist";

export const updateFrequencyCappingData = ({
    lottieFrequencyDataObject = {},
    freqCapId,
    sessionFreqCapKey,
    sid,
    lottieLocalStorageKey,
    hasCountCap,
    hasSessionCap,
    hasRestaurantSessionCap,
}: {
    lottieFrequencyDataObject: Record<string, any>;
    freqCapId: string;
    sessionFreqCapKey: string;
    sid: string;
    lottieLocalStorageKey: string;
    hasCountCap: boolean;
    hasSessionCap: boolean;
    hasRestaurantSessionCap: boolean;
}) => {
    const newSessionObject = {
        // Only add these if they're enabled
        ...(hasCountCap && freqCapId && { [freqCapId]: (lottieFrequencyDataObject[freqCapId] || 0) + 1 }),
        ...(hasRestaurantSessionCap && sessionFreqCapKey && { [sessionFreqCapKey]: 1 }),
        ...(hasSessionCap && sid && { [sid]: (lottieFrequencyDataObject[sid] || 0) + 1 }),
    };

    // Only update localStorage if we have changes to save
    const hasChanges =
        (hasCountCap && freqCapId) || (hasRestaurantSessionCap && sessionFreqCapKey) || (hasSessionCap && sid);

    if (hasChanges) {
        try {
            localStorage.setItem(lottieLocalStorageKey, JSON.stringify(newSessionObject));
        } catch (_) {
            // empty catch
        }
    }

    return newSessionObject;
};

export const isFrequencyCapReached = (sessionObject: Record<string, any>, capId: string, maxCount: number): boolean =>
    sessionObject[capId] >= maxCount;

export const updateLottieHeaderState = ({
    freqCapId = "",
    count,
    sessionCount,
    lottieLocalStorageKey,
    lottieFrequencyDataObject,
    sid,
    rxId,
    sessionFreqCapKey,
    newCartHeaderState,
    sendAnalytics,
    isFrequencyCapEnabled,
}: IUpdateCartHeaderState) => {
    const hasCountBasedFreqCapping = freqCapId && lottieFrequencyDataObject[freqCapId];
    const hasSessionBasedFreqCapping = sid && lottieFrequencyDataObject[sid];
    const hasRxSessionFreqCapping = sessionFreqCapKey && lottieFrequencyDataObject[sessionFreqCapKey];
    let shouldHideTheme = false;
    const hasFrequencyCapData = hasCountBasedFreqCapping || hasSessionBasedFreqCapping || hasRxSessionFreqCapping;
    const isRxIdNotEmpty = !isEmpty(rxId);
    const isSessionShown = lottieFrequencyDataObject[sessionFreqCapKey] === 1;
    const isSessionCapDefined = isFrequencyCapEnabled && typeof sessionCount === "number" && sessionCount > 0;
    const isCountCapDefined =
        isFrequencyCapEnabled && typeof count === "number" && count > 0 && typeof freqCapId === "string";
    const isSessionCapCrossed =
        isSessionCapDefined &&
        typeof sessionCount === "number" &&
        (lottieFrequencyDataObject[sid] || 0) >= sessionCount;
    let finalCartHeaderState = { ...newCartHeaderState };

    if (isFrequencyCapEnabled) {
        // Check if we have any frequency capping data
        if (hasFrequencyCapData) {
            // 1. Restaurant per session capping (rxId-sid) - if restaurant ID exists and session already shown
            if (isRxIdNotEmpty && isSessionShown) {
                finalCartHeaderState = {
                    ...finalCartHeaderState,
                    showFinalState: true,
                    themeChange: true,
                    hideTheme: false,
                };
            }
            // 2. Session based capping - if defined and crossed
            else if (isSessionCapDefined && isSessionCapCrossed) {
                shouldHideTheme = true;
            }
            // 3. Total count based capping - if defined and reached
            else if (
                isCountCapDefined &&
                typeof count === "number" &&
                isFrequencyCapReached(lottieFrequencyDataObject, freqCapId, count)
            ) {
                if (!isSessionShown) {
                    shouldHideTheme = true;
                }
            }
            // If no caps reached but they're defined, update the frequency cap data
            else {
                // Only update if there's at least one capping type in use
                if (isCountCapDefined || isSessionCapDefined || isRxIdNotEmpty) {
                    sendAnalytics();
                    updateFrequencyCappingData({
                        lottieFrequencyDataObject,
                        freqCapId: freqCapId || "",
                        sessionFreqCapKey,
                        sid,
                        lottieLocalStorageKey,
                        hasCountCap: isCountCapDefined,
                        hasSessionCap: isSessionCapDefined,
                        hasRestaurantSessionCap: isRxIdNotEmpty,
                    });
                }
            }
        }
        // Initialize frequency capping if not present but at least one cap type is defined
        else if (isCountCapDefined || isSessionCapDefined || isRxIdNotEmpty) {
            sendAnalytics();
            updateFrequencyCappingData({
                lottieFrequencyDataObject: {},
                freqCapId: freqCapId || "",
                sessionFreqCapKey,
                sid,
                lottieLocalStorageKey,
                hasCountCap: isCountCapDefined,
                hasSessionCap: isSessionCapDefined,
                hasRestaurantSessionCap: isRxIdNotEmpty,
            });
        }
    } else {
        // when frequency capping is disabled
        finalCartHeaderState = {
            ...finalCartHeaderState,
            hideTheme: false,
            showFinalState: false,
        };
    }

    if (shouldHideTheme) {
        return {
            shouldHideTheme,
            finalCartHeaderState: {
                ...INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE.cartHeaderLottieState,
                hideTheme: true,
            },
        };
    }

    return { shouldHideTheme, finalCartHeaderState };
};
